import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './ObservationTimeline.css';
import { websocketService } from '../services/websocketService';
import ConfirmDialog from './ConfirmDialog';
import { getAccessToken } from '../services/authStorage';
import { interpretTimelineResponse } from '../utils/timelineResponse';
import { ensureUTC } from '../utils/utcTime';
import { isDirectControl, DIRECT_CONTROL_BAND_STYLE, reservationLabel } from '../utils/reservationStyle';

// Past-nights navigation helpers: shift a 'YYYY-MM-DD' string by whole days (UTC-safe, no tz drift).
const shiftDateStr = (dateStr, deltaDays) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const ObservationTimeline = () => {
  const navigate = useNavigate();
  const { observatoryId } = useParams();
  const [timeline, setTimeline] = useState(null);
  const [observatory, setObservatory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Track completed observations that should persist until dawn
  const [completedObservations, setCompletedObservations] = useState([]);
  const completedObsRef = useRef([]);
  // Past-nights navigation: nightDate = requested night (YYYY-MM-DD) or null for the live current
  // night; displayedDate = the date the server actually returned (anchors prev/next stepping).
  const [nightDate, setNightDate] = useState(null);
  // Admin- and portal-allocated reservations, drawn as blocked bands. Without these the page
  // shows an empty night for a telescope deliberately taken out of the dispatch pool, which
  // reads as "nothing scheduled" rather than "not available".
  const [reservations, setReservations] = useState([]);
  const [displayedDate, setDisplayedDate] = useState(null);
  const isPastView = nightDate !== null;

  // #1309b: the server returns a `computing` placeholder on a cold cache miss (it solves in
  // the background instead of blocking). Show a "Computing…" state and re-poll with backoff
  // until the plan lands — WITHOUT wiping any bars we're already showing, and without
  // treating it as an error. Runs for past-night views too (start_date is the top cold source).
  const [computing, setComputing] = useState(false);
  const [noSchedule, setNoSchedule] = useState(false); // a valid response with no night at all
  const [refreshing, setRefreshing] = useState(false); // manual Refresh — must not blank the view
  const rePollRef = useRef(null);
  const rePollAttemptRef = useRef(0);
  const fetchGenRef = useRef(0);   // generation guard: drop a stale response after an observatory switch
  const fetchDataRef = useRef(null); // ref-to-latest fetchData so a scheduled re-poll isn't a stale closure

  const cancelRePoll = useCallback(() => {
    if (rePollRef.current) { clearTimeout(rePollRef.current); rePollRef.current = null; }
  }, []);
  const scheduleRePoll = useCallback(() => {
    cancelRePoll();
    const delay = Math.min(20000, 5000 * 2 ** rePollAttemptRef.current); // 5s → 10s → 20s (capped)
    rePollAttemptRef.current += 1;
    rePollRef.current = setTimeout(() => { if (fetchDataRef.current) fetchDataRef.current(); }, delay);
  }, [cancelRePoll]);
  // Clear any pending re-poll on unmount.
  useEffect(() => () => cancelRePoll(), [cancelRePoll]);

  // Auto-refresh every 5 minutes -- ONLY for the live current night. A past night is static, so it
  // must not auto-refresh (nor accept live WebSocket merges below).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Reservations for the band overlay. Fetched separately because the multi-day-timeline
  // payload does not carry them, and refreshed on focus/visibility so one created elsewhere --
  // including by the OCS portal, with no page open in this browser -- does not sit invisible
  // until a manual reload.
  useEffect(() => {
    if (!observatoryId) return undefined;
    let cancelled = false;
    const loadReservations = async () => {
      try {
        const response = await fetch(
          `/api/v1/observatories/${observatoryId}/reservations?include_cancelled=1`,
          { headers: { Authorization: `Bearer ${getAccessToken()}` } },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!cancelled) setReservations(payload?.data || []);
      } catch (e) {
        // Fail soft: no bands is the pre-existing behaviour, not a broken page.
        if (!cancelled) setReservations([]);
      }
    };
    loadReservations();
    const timer = setInterval(loadReservations, 60000);
    const onFocus = () => loadReservations();
    const onVisibility = () => { if (document.visibilityState === 'visible') loadReservations(); };
    const onMessage = (event) => {
      if (event?.data?.type === 'reservationsChanged') loadReservations();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('message', onMessage);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', onMessage);
    };
  }, [observatoryId]);

  useEffect(() => {
    if (nightDate !== null) return;
    const interval = setInterval(() => {
      fetchData();
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [observatoryId, nightDate]);

  // Initial load + reload when navigating to a different night. Cancel any pending
  // computing re-poll when the observatory/night changes (or on unmount).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (observatoryId) {
      fetchData();
    }
    return () => cancelRePoll();
  }, [observatoryId, nightDate]);

  // Update current time every 30 seconds for the time indicator
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Handle WebSocket observation updates to track completed observations
  const handleObservationUpdate = useCallback((data) => {
    if (!data) return;

    console.log('Timeline received observation update:', data);

    // Track completed observations for persistence
    if (data.status === 'completed' || data.status === 'failed' || data.status === 'aborted') {
      const obsId = data.observation_id;
      if (obsId && !completedObsRef.current.find(o => o.observation_id === obsId)) {
        // Fetch the observation details to display on timeline
        fetch(`/api/v1/observations/${obsId}`, {
          headers: {
            'Authorization': `Bearer ${getAccessToken()}`
          }
        })
          .then(res => res.json())
          .then(obsData => {
            if (obsData.success && obsData.data) {
              const obs = obsData.data;
              const completedObs = {
                observation_id: obs._id,
                target_name: obs.name || obs.targets?.[0]?.name || 'Unknown',
                start_time: obs.started_at || obs.created_at,
                end_time: obs.completed_at || obs.failed_at || obs.aborted_at || new Date().toISOString(),
                duration_minutes: obs.exposure_tracking?.total_exposures_completed || 0,
                status: data.status,
                metadata: {
                  filters: obs.targets?.[0]?.exposures?.map(e => e.filter) || []
                }
              };
              completedObsRef.current = [...completedObsRef.current, completedObs];
              setCompletedObservations([...completedObsRef.current]);
            }
          })
          .catch(err => console.error('Failed to fetch completed observation:', err));
      }
    }
  }, []);

  // Subscribe to WebSocket observation updates
  useEffect(() => {
    websocketService.on('observation_updated', handleObservationUpdate);

    return () => {
      websocketService.off('observation_updated', handleObservationUpdate);
    };
  }, [handleObservationUpdate]);

  const fetchData = async (bypassCache = false) => {
    if (!observatoryId) {
      setError('No observatory selected');
      setLoading(false);
      return;
    }
    const gen = ++fetchGenRef.current; // supersede any in-flight/scheduled fetch (observatory switch)

    try {
      // Only blank on a TRUE first load (no data yet). A background refresh / computing
      // re-poll keeps whatever we're already showing.
      if (!timeline) setLoading(true);

      // Observatory details (for filters) — best-effort.
      const obsResponse = await fetch(`/api/v1/observatories/${observatoryId}`, {
        headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      });
      if (gen !== fetchGenRef.current) return; // a newer fetch superseded this one
      if (obsResponse.ok) {
        const obsData = await obsResponse.json();
        if (obsData.success) setObservatory(obsData.data);
      }

      // Use the multi-day endpoint with days=1 for tonight's schedule.
      const cacheParam = bypassCache ? '&use_cache=false' : '';
      const dateParam = nightDate ? `&start_date=${nightDate}` : '';
      const response = await fetch(
        `/api/v1/scheduler/multi-day-timeline?observatory_id=${observatoryId}&days=1${cacheParam}${dateParam}`,
        { headers: { 'Authorization': `Bearer ${getAccessToken()}` } },
      );
      const data = await response.json().catch(() => null);
      if (gen !== fetchGenRef.current) return; // stale response — a newer fetch is in flight

      const result = interpretTimelineResponse(response.ok, data);

      if (result.status === 'computing') {
        // Cold cache: the scheduler is solving in the background. Keep any prior bars,
        // flag "Computing…", and re-poll with backoff. Do NOT setError / overwrite timeline.
        setComputing(true);
        setNoSchedule(false);
        scheduleRePoll();
        return;
      }

      // A real answer (data / empty / error) ends the computing cycle.
      cancelRePoll();
      rePollAttemptRef.current = 0;
      setComputing(false);

      if (result.status === 'data') {
        const tonightData = result.nights[0];
        setDisplayedDate(tonightData.date || null);
        setTimeline({
          timeline: tonightData.timeline || [],
          night_info: tonightData.night_info,
          summary: tonightData.summary,
          observatory: result.data.observatory,
        });
        setLastUpdated(new Date());
        setNoSchedule(false);
        setError(null);
      } else if (result.status === 'empty') {
        // A successful response with no night at all (rare — a genuinely empty night with
        // observations renders normally as 'data'). Show a friendly empty state.
        setTimeline(null);
        setNoSchedule(true);
        setError(null);
      } else { // 'error'
        // Keep a good timeline on a transient failure (e.g. a Refresh 5xx) — the error
        // renders full-screen only when there's nothing already on screen.
        setError(result.message || 'Failed to load timeline');
      }
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      console.error('Error fetching timeline:', err);
      setError(err.message);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  };
  fetchDataRef.current = fetchData; // keep the re-poll pointed at the latest closure

  // ROBUST position calculation - single source of truth for ALL positioning
  const calculateTimelinePosition = (timeStr, nightStart, nightEnd) => {
    if (!timeStr || !nightStart || !nightEnd) {
      return 0;
    }
    
    try {
      // Parse times - ensure UTC interpretation
      // The times from the server are in UTC but may not have 'Z' suffix
      const time = new Date(ensureUTC(timeStr)).getTime();
      const start = new Date(ensureUTC(nightStart)).getTime();
      const end = new Date(ensureUTC(nightEnd)).getTime();
      
      if (isNaN(time) || isNaN(start) || isNaN(end)) {
        console.error('Invalid date parsing:', { timeStr, nightStart, nightEnd });
        return 0;
      }
      
      if (end <= start) {
        console.error('End before start:', { start: new Date(start), end: new Date(end) });
        return 0;
      }
      
      const position = ((time - start) / (end - start)) * 100;
      
      // Debug log for first few calls
      if (window.debugTimelineCount === undefined) {
        window.debugTimelineCount = 0;
      }
      if (window.debugTimelineCount < 5) {
        console.log('Timeline position calc:', {
          input: timeStr,
          parsed: new Date(time).toISOString(),
          position: position.toFixed(1) + '%'
        });
        window.debugTimelineCount++;
      }
      
      return Math.max(0, Math.min(100, position));
    } catch (err) {
      console.error('Error calculating position:', err);
      return 0;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    // Ensure UTC parsing
    const date = new Date(ensureUTC(dateStr));
    // Use UTC methods to format time
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getFilterColor = (filters) => {
    if (!filters || filters.length === 0) return '#666';
    
    // Multiple filters - return array for striped pattern
    if (filters.length > 1) {
      return filters.map(filter => getSingleFilterColor(filter));
    }
    
    // Single filter
    return getSingleFilterColor(filters[0]);
  };
  
  const getSingleFilterColor = (filter) => {
    const colorMap = {
      'L': '#ddd',      // Luminance - white/light gray
      'R': '#ff4444',   // Red
      'G': '#66cc66',   // Green
      'B': '#4488ff',   // Blue
      'Ha': '#cc0044',  // H-alpha - deep red
      'HA': '#cc0044',  // H-alpha alternate
      'OIII': '#00cccc',// Oxygen III - cyan
      'SII': '#ff6600', // Sulfur II - orange
      'NII': '#ff0099', // Nitrogen II - magenta
      'Clear': '#eeeeee', // Clear/unfiltered
      'C': '#eeeeee',   // Clear alternate
      'V': '#88cc44',   // Visual - yellow-green
      'I': '#990000',   // Infrared - dark red
      'UV': '#8800ff',  // Ultraviolet - purple
      'IR': '#990000',  // Infrared alternate
      'CBB': '#aaccff' // Clear Blue Blocking - light blue
    };
    
    return colorMap[filter] || '#888';
  };
  
  const getFilterStyle = (filters) => {
    const color = getFilterColor(filters);
    
    // Single color - return as backgroundColor
    if (typeof color === 'string') {
      return { backgroundColor: color };
    }
    
    // Multiple colors - create repeating linear gradient (stripes)
    if (Array.isArray(color) && color.length > 1) {
      const stripeWidth = 100 / color.length;
      const gradientStops = [];
      
      color.forEach((c, index) => {
        const start = index * stripeWidth;
        const end = (index + 1) * stripeWidth;
        gradientStops.push(`${c} ${start}%`);
        gradientStops.push(`${c} ${end}%`);
      });
      
      return {
        background: `linear-gradient(90deg, ${gradientStops.join(', ')})`
      };
    }
    
    // Fallback
    return { backgroundColor: '#888' };
  };

  // Get status-based CSS class for observation bars
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'assigned':
        return 'status-assigned';
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
      case 'completed_series':
        return 'status-completed';
      case 'partially_completed':
        return 'status-partial';
      case 'failed':
        return 'status-failed';
      case 'aborted':
        return 'status-aborted';
      case 'expired':
        return 'status-expired';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  // Get status icon for display
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'assigned':
        return '📋';
      case 'in_progress':
        return '▶️';
      case 'completed':
      case 'completed_series':
        return '✓';
      case 'partially_completed':
        return '◐';
      case 'failed':
        return '✗';
      case 'aborted':
        return '⏹';
      case 'expired':
        return '⏰';
      case 'cancelled':
        return '⊘';
      default:
        return '';
    }
  };

  // Generate filter legend based on observatory's actual available filters
  const generateFilterLegend = () => {
    if (!observatory || !observatory.capabilities || !observatory.capabilities.available_filters) {
      // Default legend if no observatory data
      return [
        { filter: 'C', label: 'Clear', color: '#eeeeee' },
        { filter: 'R', label: 'Red', color: '#ff4444' },
        { filter: 'V', label: 'Visual', color: '#88cc44' },
        { filter: 'B', label: 'Blue', color: '#4488ff' }
      ];
    }
    
    const filterLabels = {
      'C': 'Clear',
      'B': 'Blue',
      'V': 'Visual',
      'R': 'Red',
      'I': 'Infrared',
      'HA': 'H-alpha',
      'Ha': 'H-alpha',
      'OIII': 'Oxygen III',
      'SII': 'Sulfur II',
      'NII': 'Nitrogen II',
      'L': 'Luminance',
      'G': 'Green',
      'CBB': 'Clear Blue Blocking',
      'UV': 'Ultraviolet',
      'IR': 'Infrared'
    };
    
    return observatory.capabilities.available_filters.map(filter => ({
      filter: filter,
      label: filterLabels[filter] || filter,
      color: getSingleFilterColor(filter)
    }));
  };

  // Handle context menu
  const handleContextMenu = (e, observation) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      observation
    });
  };

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleDeleteObservation = async () => {
    const observation = contextMenu?.observation;
    // Close the context menu immediately; the confirm dialog takes over.
    setContextMenu(null);
    if (!observation) {
      return;
    }
    setConfirm({
      title: 'Delete observation?',
      message: `Delete observation "${observation.target_name}"?`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        let response;
        try {
          response = await fetch(`/api/v1/observations/${observation.observation_id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${getAccessToken()}`
            }
          });
        } catch (error) {
          alert(`Failed to delete: ${error.message}`);
          throw error;
        }

        if (response.ok) {
          await fetchData();
        } else {
          const data = await response.json().catch(() => ({}));
          alert(`Failed to delete: ${data.error || 'Unknown error'}`);
          throw new Error(data.error || 'Failed to delete observation');
        }
      },
    });
  };

  // Full-screen states apply only when there's nothing already on screen — a computing
  // re-poll or a transient error must never wipe a timeline we're already showing (#1309b).
  if (loading && !timeline) {
    return <div className="timeline-container">Loading timeline...</div>;
  }

  if (computing && !timeline) {
    return (
      <div className="timeline-container">
        ⏳ Computing schedule…{rePollAttemptRef.current > 3 ? ' (taking longer than usual)' : ''}
      </div>
    );
  }

  if (noSchedule && !timeline) {
    return <div className="timeline-container">No observations scheduled for this night.</div>;
  }

  if (error && !timeline) {
    return <div className="timeline-container error">Error: {error}</div>;
  }

  if (!timeline) {
    return <div className="timeline-container">No timeline data available</div>;
  }

  const { night_info, timeline: observations, summary } = timeline;
  
  // Helper to ensure UTC parsing
  // Calculate rounded timeline boundaries (same as used for hour labels)
  const darkStart = new Date(ensureUTC(night_info.twilight_evening_end));
  const darkEnd = new Date(ensureUTC(night_info.twilight_morning_start));
  
  const timelineStart = new Date(darkStart);
  timelineStart.setUTCMinutes(0, 0, 0);
  timelineStart.setUTCHours(Math.floor(darkStart.getUTCHours()));
  
  const timelineEnd = new Date(darkEnd);
  timelineEnd.setUTCMinutes(0, 0, 0);
  if (darkEnd.getUTCMinutes() > 0) {
    timelineEnd.setUTCHours(Math.ceil(darkEnd.getUTCHours()) + 1);
  } else {
    timelineEnd.setUTCHours(Math.ceil(darkEnd.getUTCHours()));
  }
  
  // Helper to calculate position on the rounded timeline
  const calculatePositionOnTimeline = (timeStr) => {
    const time = new Date(ensureUTC(timeStr));
    const totalMs = timelineEnd - timelineStart;
    const position = ((time - timelineStart) / totalMs) * 100;
    return Math.max(0, Math.min(100, position));
  };
  
  // Pre-process all observations to calculate positions
  const processedObservations = observations.map((obs, index) => {
    const isTransit = !!(obs.transit_ingress && obs.transit_egress);
    const isTimeBased = obs.is_time_based === true;
    
    // Calculate all positions using rounded timeline boundaries
    const blockStart = calculatePositionOnTimeline(obs.start_time);
    const blockEnd = calculatePositionOnTimeline(obs.end_time);
    
    let transitData = null;
    if (isTransit) {
      const ingressPos = calculatePositionOnTimeline(obs.transit_ingress);
      const egressPos = calculatePositionOnTimeline(obs.transit_egress);
      
      // Calculate marker positions RELATIVE to the observation block
      const blockWidth = blockEnd - blockStart;
      const ingressRelative = blockWidth > 0 ? ((ingressPos - blockStart) / blockWidth) * 100 : 0;
      const egressRelative = blockWidth > 0 ? ((egressPos - blockStart) / blockWidth) * 100 : 0;
      
      transitData = {
        ingressPos: ingressRelative,
        egressPos: egressRelative,
        ingressTime: obs.transit_ingress,
        egressTime: obs.transit_egress
      };
    }
    
    return {
      observation: obs,
      index,
      isTransit,
      isTimeBased,
      blockStart,
      blockEnd,
      blockWidth: blockEnd - blockStart,
      transitData,
      verticalPosition: 20 + (index % 3) * 35
    };
  });

  return (
    <div className="timeline-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{observatory?.name || 'Observatory'}{observatory?.code ? ` (${observatory.code})` : ''} - {night_info.date}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {lastUpdated && !isPastView && (
            <span style={{ fontSize: '0.9em', color: '#7f8c8d' }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          {/* Past-nights navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isPastView && (
              <span style={{ fontSize: '0.8em', color: '#e67e22', fontWeight: 600 }} title="Viewing a past night (static)">◐ past</span>
            )}
            <button
              onClick={() => setNightDate(shiftDateStr(displayedDate || todayStr(), -1))}
              title="Previous night"
              style={{ padding: '5px 8px', cursor: 'pointer' }}
            >◀</button>
            <input
              type="date"
              value={displayedDate || ''}
              max={todayStr()}
              onChange={(e) => setNightDate(e.target.value && e.target.value !== todayStr() ? e.target.value : null)}
              title="Jump to a night"
              style={{ padding: '4px', cursor: 'pointer' }}
            />
            <button
              onClick={() => {
                const nd = shiftDateStr(displayedDate || todayStr(), 1);
                setNightDate(nd >= todayStr() ? null : nd);
              }}
              disabled={!isPastView}
              title="Next night"
              style={{ padding: '5px 8px', cursor: isPastView ? 'pointer' : 'not-allowed', opacity: isPastView ? 1 : 0.4 }}
            >▶</button>
            {isPastView && (
              <button
                onClick={() => setNightDate(null)}
                title="Return to the live current night"
                style={{ padding: '5px 10px', cursor: 'pointer', background: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px' }}
              >Current</button>
            )}
          </div>
          <button
            onClick={() => navigate(`/multi-day-timeline/${observatoryId}`)}
            style={{
              padding: '5px 10px',
              cursor: 'pointer',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px'
            }}
          >
            📅 Multi-Day View
          </button>
          <button
            onClick={async () => {
              // #1309b: use a local `refreshing` flag, NOT the global `loading`, so a manual
              // refresh keeps the current schedule on screen instead of blanking it.
              setRefreshing(true);
              await fetchData(true); // bypass cache — force a fresh solve
              setRefreshing(false);
            }}
            disabled={refreshing}
            style={{
              padding: '5px 10px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              opacity: refreshing ? 0.5 : 1
            }}
          >
            {refreshing ? 'Refreshing…' : '🔄 Refresh'}
          </button>
          {computing && (
            <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#888' }}>⏳ Computing…</span>
          )}
        </div>
      </div>
      
      <div className="night-summary">
        <div className="summary-item">
          <strong>Observatory:</strong> {timeline.observatory.name}
        </div>
        <div className="summary-item">
          <strong>Dark Time (UTC):</strong> {formatTime(night_info.twilight_evening_end)} - {formatTime(night_info.twilight_morning_start)}
          <span className="detail"> ({Math.round(night_info.total_dark_hours * 60)} minutes)</span>
        </div>
        <div className="summary-item">
          <strong>Moon:</strong> {(night_info.moon.illumination * 100).toFixed(0)}% illuminated
          {night_info.moon.rise && <span className="detail"> (rises {formatTime(night_info.moon.rise)})</span>}
        </div>
        <div className="summary-item">
          <strong>Schedule:</strong> {summary.total_observations} observations, 
          {' '}{formatDuration(summary.total_observation_time)} total
          <span className="detail"> ({summary.utilization_percent.toFixed(0)}% utilization)</span>
        </div>
      </div>

      <div className="gantt-container">
        <div className="time-axis">
          {/* Generate hourly time labels */}
          {(() => {
            // Ensure UTC parsing by adding Z if needed
            // Parse actual dark time boundaries
            const darkStart = new Date(ensureUTC(night_info.twilight_evening_end));
            const darkEnd = new Date(ensureUTC(night_info.twilight_morning_start));
            
            // Round timeline boundaries to nearest hours
            const startTime = new Date(darkStart);
            startTime.setUTCMinutes(0, 0, 0);
            startTime.setUTCHours(Math.floor(darkStart.getUTCHours()));
            
            const endTime = new Date(darkEnd);
            endTime.setUTCMinutes(0, 0, 0);
            if (darkEnd.getUTCMinutes() > 0) {
              endTime.setUTCHours(Math.ceil(darkEnd.getUTCHours()) + 1);
            } else {
              endTime.setUTCHours(Math.ceil(darkEnd.getUTCHours()));
            }
            
            const totalHours = (endTime - startTime) / (1000 * 60 * 60);
            
            // Debug logging
            console.log('Timeline generation debug:', {
              startTimeStr: night_info.twilight_evening_end,
              endTimeStr: night_info.twilight_morning_start,
              startTime: startTime.toISOString(),
              endTime: endTime.toISOString(),
              startUTCHours: startTime.getUTCHours(),
              endUTCHours: endTime.getUTCHours(),
              totalHours
            });
            
            const labels = [];
            
            // Generate labels for each hour from start to end
            for (let i = 0; i <= totalHours; i++) {
              const labelTime = new Date(startTime.getTime() + i * 60 * 60 * 1000);
              const position = (i / totalHours) * 100;
              
              labels.push(
                <div 
                  key={i} 
                  className="time-label" 
                  style={{ left: `${position}%` }}
                >
                  {labelTime.getUTCHours().toString().padStart(2, '0')}:00
                </div>
              );
            }
            
            return labels;
          })()}
        </div>

        <div className="gantt-chart">
          {/* Grid lines */}
          <div className="grid-lines">
            {(() => {
              const lines = [];
              // Ensure UTC parsing by adding Z if needed
              const startTime = new Date(ensureUTC(night_info.twilight_evening_end));
              const endTime = new Date(ensureUTC(night_info.twilight_morning_start));
              const totalMinutes = (endTime - startTime) / (1000 * 60);
              
              for (let i = 0; i <= totalMinutes; i += 30) {
                const position = (i / totalMinutes) * 100;
                const isHourLine = i % 60 === 0;
                
                lines.push(
                  <div 
                    key={i} 
                    className={`grid-line ${isHourLine ? 'hour-line' : 'half-hour-line'}`}
                    style={{ left: `${position}%` }}
                  />
                );
              }
              
              return lines;
            })()}
          </div>

          {/* Moon indicator */}
          {night_info.moon.rise && (
            <div 
              className="moon-indicator"
              style={{
                left: `${calculateTimelinePosition(night_info.moon.rise, night_info.twilight_evening_end, night_info.twilight_morning_start)}%`
              }}
              title={`Moonrise: ${formatTime(night_info.moon.rise)}`}
            >
              🌙
            </div>
          )}

          {/* Dark time indicator */}
          {(() => {
            // Calculate dark time position relative to rounded timeline
            const darkStart = new Date(ensureUTC(night_info.twilight_evening_end));
            const darkEnd = new Date(ensureUTC(night_info.twilight_morning_start));
            
            // Timeline boundaries (rounded)
            const timelineStart = new Date(darkStart);
            timelineStart.setUTCMinutes(0, 0, 0);
            timelineStart.setUTCHours(Math.floor(darkStart.getUTCHours()));
            
            const timelineEnd = new Date(darkEnd);
            timelineEnd.setUTCMinutes(0, 0, 0);
            if (darkEnd.getUTCMinutes() > 0) {
              timelineEnd.setUTCHours(Math.ceil(darkEnd.getUTCHours()) + 1);
            } else {
              timelineEnd.setUTCHours(Math.ceil(darkEnd.getUTCHours()));
            }
            
            const totalMs = timelineEnd - timelineStart;
            const darkStartPos = ((darkStart - timelineStart) / totalMs) * 100;
            const darkEndPos = ((darkEnd - timelineStart) / totalMs) * 100;
            const darkWidth = darkEndPos - darkStartPos;
            
            return (
              <>
                {/* Reserved bands.
                    Drawn before the dark-time background so the blue night wash sits on top and
                    the two read as layers rather than competing fills. Positioned with the same
                    helper as everything else on this timeline, and clipped to it: a reservation
                    outside the drawn night contributes nothing, which is correct rather than
                    missing -- this page only ever shows one night.
                    [start, effective_blocking_end] is the blocking window: an active row blocks
                    its full span, a cancelled one only the part it actually held, and a row
                    cancelled before it began blocks nothing at all. */}
                {(reservations || []).map((r) => {
                  const startMs = new Date(ensureUTC(r.start_time)).getTime();
                  const endMs = new Date(ensureUTC(r.effective_blocking_end || r.end_time)).getTime();
                  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
                  if (endMs <= timelineStart.getTime() || startMs >= timelineEnd.getTime()) return null;
                  const left = calculatePositionOnTimeline(r.start_time);
                  const right = calculatePositionOnTimeline(r.effective_blocking_end || r.end_time);
                  const width = right - left;
                  if (width <= 0) return null;
                  const held = `${new Date(startMs).toISOString().slice(11, 16)}–${new Date(endMs).toISOString().slice(11, 16)} UTC`;
                  return (
                    <div
                      key={r.id || r._id}
                      style={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${width}%`,
                        height: '100%',
                        backgroundColor: 'rgba(255, 152, 0, 0.18)',
                        backgroundImage:
                          'repeating-linear-gradient(45deg, rgba(255,152,0,0.35) 0, rgba(255,152,0,0.35) 4px, transparent 4px, transparent 8px)',
                        borderLeft: '2px solid rgba(255,152,0,0.8)',
                        borderRight: '2px solid rgba(255,152,0,0.8)',
                        zIndex: 2,
                        pointerEvents: 'auto',
                        // Direct Control windows: the operations page's interactive-session blue.
                        ...(isDirectControl(r) ? DIRECT_CONTROL_BAND_STYLE : {}),
                      }}
                      title={`${reservationLabel(r)}: ${r.title || 'Observatory reservation'}${r.purpose ? ` — ${r.purpose}` : ''} (${held})`}
                    />
                  );
                })}
                {/* Dark time background */}
                <div 
                  className="dark-time-indicator"
                  style={{
                    position: 'absolute',
                    left: `${darkStartPos}%`,
                    width: `${darkWidth}%`,
                    height: '100%',
                    backgroundColor: 'rgba(0, 100, 200, 0.1)',
                    borderLeft: '2px solid rgba(0, 100, 200, 0.5)',
                    borderRight: '2px solid rgba(0, 100, 200, 0.5)',
                    zIndex: 1
                  }}
                  title={`Dark time: ${formatTime(night_info.twilight_evening_end)} - ${formatTime(night_info.twilight_morning_start)}`}
                />
                {/* Dark time start marker */}
                <div 
                  style={{
                    position: 'absolute',
                    left: `${darkStartPos}%`,
                    top: '-20px',
                    fontSize: '10px',
                    color: 'rgba(0, 100, 200, 0.8)',
                    fontWeight: 'bold'
                  }}
                >
                  ▼ Dark
                </div>
                {/* Dark time end marker */}
                <div 
                  style={{
                    position: 'absolute',
                    left: `${darkEndPos}%`,
                    top: '-20px',
                    transform: 'translateX(-100%)',
                    fontSize: '10px',
                    color: 'rgba(0, 100, 200, 0.8)',
                    fontWeight: 'bold'
                  }}
                >
                  Dark ▼
                </div>
              </>
            );
          })()}

          {/* Current time indicator */}
          {(() => {
            const darkStart = new Date(ensureUTC(night_info.twilight_evening_end));
            const darkEnd = new Date(ensureUTC(night_info.twilight_morning_start));

            // Timeline boundaries (rounded)
            const localTimelineStart = new Date(darkStart);
            localTimelineStart.setUTCMinutes(0, 0, 0);
            localTimelineStart.setUTCHours(Math.floor(darkStart.getUTCHours()));

            const localTimelineEnd = new Date(darkEnd);
            localTimelineEnd.setUTCMinutes(0, 0, 0);
            if (darkEnd.getUTCMinutes() > 0) {
              localTimelineEnd.setUTCHours(Math.ceil(darkEnd.getUTCHours()) + 1);
            } else {
              localTimelineEnd.setUTCHours(Math.ceil(darkEnd.getUTCHours()));
            }

            const totalMs = localTimelineEnd - localTimelineStart;
            const nowMs = currentTime.getTime();
            const currentPos = ((nowMs - localTimelineStart) / totalMs) * 100;

            // Only show if current time is within the timeline range
            if (currentPos < 0 || currentPos > 100) {
              return null;
            }

            return (
              <div
                className="current-time-indicator"
                style={{
                  position: 'absolute',
                  left: `${currentPos}%`,
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: '#e74c3c',
                  zIndex: 20,
                  boxShadow: '0 0 6px rgba(231, 76, 60, 0.8)'
                }}
                title={`Current time: ${currentTime.getUTCHours().toString().padStart(2, '0')}:${currentTime.getUTCMinutes().toString().padStart(2, '0')} UTC`}
              >
                {/* Current time label */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-18px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  NOW
                </div>
                {/* Animated pulse at bottom */}
                <div
                  className="time-indicator-pulse"
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#e74c3c',
                    borderRadius: '50%',
                    animation: 'pulse 2s infinite'
                  }}
                />
              </div>
            );
          })()}

          {/* Observation blocks */}
          {processedObservations.map(po => {
            const obs = po.observation;
            const filters = obs.metadata?.filters || obs.filters || [];
            const filterDisplay = filters.length > 0 ? filters.join(', ') : 'No filter';
            const status = obs.status || 'pending';

            return (
              // Key by start_time too: a repetitive series now emits one timeline entry PER iteration
              // that ran tonight (same observation_id), so keying on the id alone collided and dropped
              // the extra iterations. (repetitive-timeline-fidelity)
              <div
                key={`block-${obs.observation_id}-${obs.start_time}`}
                className={`observation-block ${po.isTransit ? 'transit-observation' : ''} ${po.isTimeBased ? 'time-based' : ''} ${obs.scheduled === false ? 'eligible-unscheduled' : getStatusClass(status)}`}
                style={{
                  position: 'absolute',
                  left: `${po.blockStart}%`,
                  width: `${po.blockWidth}%`,
                  height: '30px',
                  top: `${po.verticalPosition}px`,
                  ...getFilterStyle(filters),
                  cursor: 'pointer',
                  zIndex: 5
                }}
                onClick={() => navigate(`/observations/${obs.observation_id}`)}
                onContextMenu={(e) => handleContextMenu(e, obs)}
                title={(() => {
                  // Unified schedule-bar tooltip — name, observer, priority,
                  // scheduled time (HH:MM-HH:MM UTC), filter, status — same
                  // order as the MUI Tooltip variants in TimelineView and
                  // ObservatorySchedulePulse. formatTime already emits HH:MM
                  // UTC parts, so the time line reads cleanly. Transit /
                  // altitude / min-time stay after the unified block so we
                  // don't lose info this view already surfaced.
                  const obsTitle = obs.name || obs.target_name || obs.targets?.[0]?.name || 'Observation';
                  const lines = [obsTitle];
                  if (obs.observer_name) lines.push(`Observer: ${obs.observer_name}`);
                  if (obs.priority != null) lines.push(`Priority: ${obs.priority}`);
                  lines.push(`${formatTime(obs.start_time)} - ${formatTime(obs.end_time)} UTC`);
                  lines.push(`Filter: ${filterDisplay || '—'}`);
                  lines.push(`Status: ${String(status).replace(/_/g, ' ')}`);
                  if (po.isTransit) lines.push(`Transit: ${formatTime(po.transitData.ingressTime)} - ${formatTime(po.transitData.egressTime)} UTC`);
                  if (po.isTimeBased) lines.push(`Min time: ${obs.minimum_observing_time_minutes} min`);
                  if (obs.altitude_at_start != null) lines.push(`Alt: ${obs.altitude_at_start.toFixed(0)}°`);
                  return lines.join('\n');
                })()}
              >
                {/* Status indicator - show for all statuses. An eligible-but-unscheduled bar (the
                    scheduler found it observable in this window but didn't place it) gets its own glyph. */}
                <div className={`status-badge status-badge-${status}`}>{obs.scheduled === false && status === 'pending' ? '○' : getStatusIcon(status)}</div>
                {status === 'in_progress' && (
                  <div className="status-indicator running-indicator" />
                )}

                {/* Transit markers - INSIDE the observation block */}
                {po.transitData && (
                  <>
                    <div
                      className="transit-marker ingress-marker"
                      style={{ left: `${po.transitData.ingressPos}%` }}
                      title={`Ingress: ${formatTime(po.transitData.ingressTime)}`}
                    >
                      <div className="marker-label">I</div>
                    </div>
                    <div
                      className="transit-marker egress-marker"
                      style={{ left: `${po.transitData.egressPos}%` }}
                      title={`Egress: ${formatTime(po.transitData.egressTime)}`}
                    >
                      <div className="marker-label">E</div>
                    </div>
                  </>
                )}

                <div className="obs-label">
                  <div className="obs-name">{obs.target_name}</div>
                  <div className="obs-details">
                    {po.isTimeBased && obs.minimum_observing_time_minutes ?
                      `${obs.minimum_observing_time_minutes}-${Math.round(obs.duration_minutes)}m` :
                      formatDuration(obs.duration_minutes)} • {filters.length > 0 ? filters[0] : ''} • {obs.altitude_at_start?.toFixed(0) || '?'}°
                  </div>
                </div>
              </div>
            );
          })}

          {/* Completed observations that should persist (live current-night view only; a past night's
              server timeline already contains what ran that night) */}
          {!isPastView && completedObservations.map((obs, index) => {
            // Only show if we have valid start/end times and it's not already in the processed observations
            if (!obs.start_time || !obs.end_time) return null;
            if (processedObservations.find(po => po.observation.observation_id === obs.observation_id)) return null;

            const blockStart = calculatePositionOnTimeline(obs.start_time);
            const blockEnd = calculatePositionOnTimeline(obs.end_time);
            const blockWidth = Math.max(blockEnd - blockStart, 1);
            const filters = obs.metadata?.filters || [];
            const verticalPosition = 20 + ((processedObservations.length + index) % 3) * 35;

            return (
              // Key by start_time too: a repetitive series now emits one completed block PER iteration
              // that ran tonight (same observation_id), so keying on the id alone collided and dropped
              // the extra iterations. (repetitive-timeline-fidelity)
              <div
                key={`completed-${obs.observation_id}-${obs.start_time}`}
                className={`observation-block ${getStatusClass(obs.status)}`}
                style={{
                  position: 'absolute',
                  left: `${blockStart}%`,
                  width: `${blockWidth}%`,
                  height: '30px',
                  top: `${verticalPosition}px`,
                  ...getFilterStyle(filters),
                  cursor: 'pointer',
                  zIndex: 4
                }}
                onClick={() => navigate(`/observations/${obs.observation_id}`)}
                title={`${obs.target_name} [${obs.status.toUpperCase()}]\n${formatTime(obs.start_time)} - ${formatTime(obs.end_time)}`}
              >
                <div className="status-badge">{getStatusIcon(obs.status)}</div>
                <div className="obs-label">
                  <div className="obs-name">{obs.target_name}</div>
                  <div className="obs-details">
                    {obs.status}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filter Legend */}
        <div className="legend">
          <h4>Filter Legend</h4>
          <div className="legend-items">
            {generateFilterLegend().map(item => (
              <div key={item.filter} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                <span>{item.label} ({item.filter})</span>
              </div>
            ))}
            <div className="legend-item">
              <div className="legend-color" style={{
                background: 'linear-gradient(90deg, #ff4444 0%, #ff4444 33%, #88cc44 33%, #88cc44 66%, #4488ff 66%, #4488ff 100%)'
              }}></div>
              <span>Multiple Filters (striped)</span>
            </div>
          </div>
        </div>

        {/* Status Legend */}
        <div className="legend status-legend">
          <h4>Status Legend</h4>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-status status-pending">⏳</div>
              <span>Pending</span>
            </div>
            <div className="legend-item">
              <div className="legend-status status-assigned">📋</div>
              <span>Assigned</span>
            </div>
            <div className="legend-item">
              <div className="legend-status status-in-progress">▶️</div>
              <span>Running</span>
            </div>
            <div className="legend-item">
              <div className="legend-status status-completed">✓</div>
              <span>Completed</span>
            </div>
            <div className="legend-item">
              <div className="legend-status status-failed">✗</div>
              <span>Failed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Observation Details Table */}
      <div className="observation-list">
        <h3>Observation Details</h3>
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Target</th>
              <th>Duration</th>
              <th>Filters</th>
              <th>Altitude</th>
              <th>Priority</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {observations.map(obs => (
              // start_time in the key: a repetitive series can list multiple iterations (same id) tonight.
              <tr key={`${obs.observation_id}-${obs.start_time}`}>
                <td>{formatTime(obs.start_time)}</td>
                <td>{obs.target_name}</td>
                <td>{formatDuration(obs.duration_minutes)}</td>
                <td>{obs.metadata?.filters?.join(', ') || obs.filters?.join(', ') || '-'}</td>
                <td>{obs.altitude_at_start?.toFixed(0) || '?'}°</td>
                <td>{obs.priority || '-'}</td>
                <td>{obs.score?.toFixed(2) || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            backgroundColor: '#2c3e50',
            border: '1px solid #34495e',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            zIndex: 1000
          }}
        >
          <div 
            style={{ padding: '8px 16px', cursor: 'pointer', color: '#ecf0f1' }}
            onClick={() => {
              navigate(`/observations/${contextMenu.observation.observation_id}`);
              setContextMenu(null);
            }}
          >
            View Details
          </div>
          <div 
            style={{ padding: '8px 16px', cursor: 'pointer', color: '#ecf0f1' }}
            onClick={() => {
              navigate(`/observations/${contextMenu.observation.observation_id}/edit`);
              setContextMenu(null);
            }}
          >
            Edit
          </div>
          <div 
            style={{ padding: '8px 16px', cursor: 'pointer', borderTop: '1px solid #34495e', color: '#e74c3c' }}
            onClick={handleDeleteObservation}
          >
            Delete
          </div>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
};

export default ObservationTimeline;